// types
import {
  ChartType,
  ChartSortBy,
  ChartPalette,
  ChartAxisPointer,
  ChartLabelFormat,
  ChartConfigInput,
  ChartLabelPosition,
  ChartTooltipTrigger
} from 'types/charts'
import { FieldType, ChartAggregate, ChartDateBucket } from 'types/graphql'

// chartData groups by when the record was created under this id
export const CREATED_AT_FIELD_ID = 'CREATED_AT'

export const GROUPABLE_FIELD_TYPES = [
  FieldType.PickList,
  FieldType.Lookup,
  FieldType.Switch,
  FieldType.Tag,
  FieldType.RiskMatrix,
  FieldType.Number,
  FieldType.Date
]

export const CHART_TYPE_OPTIONS = [
  { label: 'Bar chart', value: ChartType.Bar },
  { label: 'Line chart', value: ChartType.Line },
  { label: 'Pie chart', value: ChartType.Pie }
]

// A chart's tile on a dashboard: everything but its size lives in the chart
export const DEFAULT_CHART_TILE_HEIGHT = 360

// A chart's numbers are recomputed by Refresh at most this often, counted from their computedAt
export const CHART_REFRESH_INTERVAL = 5 * 60 * 1000

export const CHART_REFRESH_LOCKED_MESSAGE = `Charts can be refreshed once in ${CHART_REFRESH_INTERVAL / 60 / 1000} minutes`

export const CHART_PALETTES: Record<Exclude<ChartPalette, ChartPalette.Custom>, string[]> = {
  [ChartPalette.Default]: [
    '#5470c6',
    '#91cc75',
    '#fac858',
    '#ee6666',
    '#73c0de',
    '#3ba272',
    '#fc8452',
    '#9a60b4',
    '#ea7ccc'
  ],
  [ChartPalette.Vivid]: [
    '#6200ee',
    '#03dac6',
    '#ff0266',
    '#ffab00',
    '#00c853',
    '#2962ff',
    '#d500f9',
    '#ff6d00',
    '#00b8d4'
  ],
  [ChartPalette.Pastel]: [
    '#a0c4ff',
    '#bdb2ff',
    '#ffc6ff',
    '#ffadad',
    '#ffd6a5',
    '#fdffb6',
    '#caffbf',
    '#9bf6ff',
    '#e4c1f9'
  ],
  [ChartPalette.Cool]: [
    '#03045e',
    '#023e8a',
    '#0077b6',
    '#0096c7',
    '#00b4d8',
    '#48cae4',
    '#90e0ef',
    '#ade8f4',
    '#caf0f8'
  ],
  [ChartPalette.Warm]: [
    '#9d0208',
    '#d00000',
    '#dc2f02',
    '#e85d04',
    '#f48c06',
    '#faa307',
    '#ffba08',
    '#ffd166',
    '#ffe8a3'
  ],
  [ChartPalette.Mono]: [
    '#212529',
    '#343a40',
    '#495057',
    '#6c757d',
    '#adb5bd',
    '#ced4da',
    '#dee2e6',
    '#e9ecef',
    '#f8f9fa'
  ]
}

export const AGGREGATE_OPTIONS = [
  { label: 'Count of records', value: ChartAggregate.Count },
  { label: 'Sum', value: ChartAggregate.Sum },
  { label: 'Average', value: ChartAggregate.Avg },
  { label: 'Minimum', value: ChartAggregate.Min },
  { label: 'Maximum', value: ChartAggregate.Max }
]

export const DATE_BUCKET_OPTIONS = [
  { label: 'Day', value: ChartDateBucket.Day },
  { label: 'Week', value: ChartDateBucket.Week },
  { label: 'Month', value: ChartDateBucket.Month },
  { label: 'Quarter', value: ChartDateBucket.Quarter },
  { label: 'Year', value: ChartDateBucket.Year }
]

// Options that differ from the schema defaults per chart type: merged under a config before it is parsed, and over
// the form's when the chart type is switched. The schema does not know them
export const CHART_TYPE_DEFAULTS: Record<ChartType, Pick<ChartConfigInput, 'transform' | 'display'>> = {
  [ChartType.Bar]: {},
  [ChartType.Line]: {
    transform: { sortBy: ChartSortBy.None },
    display: { tooltip: { axisPointer: ChartAxisPointer.Line } }
  },
  [ChartType.Pie]: {
    transform: { sortBy: ChartSortBy.Value },
    display: {
      tooltip: { trigger: ChartTooltipTrigger.Item },
      labels: { show: true, position: ChartLabelPosition.Outside, format: ChartLabelFormat.NamePercent }
    }
  }
}
